import { useCallback, useState, useMemo, type ReactNode } from 'react';
import { toPercentage, toCurrencyTruncated } from '@/utilities/formatting';
import { shallowEqual, useSelector } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import { format } from 'date-fns';
import { getUTCDate } from '@utilities/date';
import { pathConstructor } from '@/hooks/useCompany/utils';
import {
  NarrativeKeyMetric,
  NarrativeKeyMetricChart,
} from './components/NarrativeKeyMetric';
import { SharePrice } from './components/SharePrice';
import {
  usePriceHistoryData,
  type HoverParam,
} from '@components/MiniPriceChart';
import { cn } from '@simplywallst/ui-core/next';
import { CURRENT_PERIOD } from './constants';

const formatter = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';
const formatTimestamp = (timestamp: number): string => {
  if (typeof timestamp !== 'number' || timestamp === 0) return 'N/A';
  return format(getUTCDate(timestamp), 'dd MMM, yyyy');
};

const PositiveNegativeValue = ({
  value,
  children,
}: {
  value: number | null;
  children: ReactNode;
}) => (
  <span
    className={cn(
      value === 0 || value === null
        ? 'text-legacy-mediumgray'
        : value > 0
          ? 'text-good'
          : 'text-bad'
    )}
  >
    {children}
  </span>
);

type Selected =
  | { status: 'loading' }
  | {
      marketCapListing: number;
      marketCapListingFormatted: string;
      tradingItemCurrencyISO: string;
      status: 'success';
      return7D: number | null;
      return7DFormatted: string;
      return1Y: number | null;
      return1YFormatted: string;
      companyId: string;
      analystCount: number;
      lastAnalysisUpdateFormatted: string;
      sharePriceFormatted: string;
    };
function selector(state: {
  company: CompanyState;
  location: LocationState;
}): Selected {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const summary = state.company.summary[companyId];
    const analysis = state.company.analysis[companyId];
    const formatting = state.company.formatting[companyId]; ////
    const tradingItemCurrencyISO = formatting.tradingItemCurrencyISO;
    if (companyId === '') {
      return {
        status: 'loading',
      };
    }

    return {
      marketCapListing: analysis.marketCapRawData.listing,
      marketCapListingFormatted: toCurrencyTruncated(
        analysis.marketCapRawData.listing,
        1,
        tradingItemCurrencyISO
      ),
      tradingItemCurrencyISO,
      status: 'success',
      return7D: analysis.return7D,
      return7DFormatted: formatter(analysis.return7D),
      return1Y: analysis.return1YrAbs,
      return1YFormatted: formatter(analysis.return1YrAbs),
      companyId,
      analystCount: summary.analystCount,
      lastAnalysisUpdateFormatted: formatTimestamp(summary.lastAnalysisUpdate),
      sharePriceFormatted: toCurrencyTruncated(
        analysis.sharePrice,
        2,
        tradingItemCurrencyISO
      ),
    };
  } catch {
    return { status: 'loading' };
  }
}

const SmallLayout = ({
  children,
  companyId,
}: {
  children: ReactNode;
  companyId: string;
}) => {
  const { isNegativeMovement } = usePriceHistoryData(companyId, CURRENT_PERIOD);
  return (
    <div className="relative max-md:px-x2 max-md:py-x1_5 dark:max-md:[&_.visx-area-closed]:hidden">
      <div className="pointer-events-none absolute inset-0 md:hidden">
        <div className="border absolute inset-0 rounded-md border-subtle shadow light:shadow-sm" />
        {isNegativeMovement !== undefined && (
          <div
            className={cn(
              'absolute inset-px rounded-md bg-legacy-noir/20 backdrop-blur-lg light:hidden',
              'animate-fade-in bg-[linear-gradient(to_top_right,var(--tw-gradient-from),var(--tw-gradient-to)_65%)] to-transparent [animation-fill-mode:backwards]',
              isNegativeMovement ? 'from-bad-subtle' : 'from-good-subtle'
            )}
          />
        )}
      </div>
      <div className="relative z-1">{children}</div>
    </div>
  );
};

const isProjection7d1yDifferent = (selected: ReturnType<typeof selector>) => {
  if (selected.status !== 'success') return false;
  return (
    !selected.return7D ||
    !selected.return1Y ||
    (selected.return7D < 0 && selected.return1Y > 0) ||
    (selected.return7D > 0 && selected.return1Y < 0)
  );
};

export const PriceAnalyticsCard = () => {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);

  const onHover = useCallback(({ time, value }: HoverParam) => {
    setSelectedTime(time);
    setSelectedPrice(value);
  }, []);

  // Highlight 7D when 1Y differs from 7D
  const show7DHighlighting = useMemo(
    () => isProjection7d1yDifferent(selected),
    [selected]
  );

  if (selected.status === 'loading') return null;

  const companyId = selected.companyId;

  return (
    <SmallLayout companyId={companyId}>
      <div className="grid grid-cols-[92px_auto] gap-1.5">
        <SharePrice
          companyId={companyId}
          time={selectedTime}
          sharePrice={selectedPrice}
          currencyISO={selected.tradingItemCurrencyISO}
        />
        <NarrativeKeyMetric companyId={companyId} sharePrice={selectedPrice} />
      </div>
      <div className="mt-2">
        <NarrativeKeyMetricChart companyId={companyId} onHover={onHover} />
      </div>
      <div className="grid grid-cols-[92px_auto] gap-2 text-xs text-soft sm:grid-cols-[70px_auto]">
        <div>
          <span className="mr-1" aria-label="1 year change">
            1Y
          </span>
          <span>
            <PositiveNegativeValue value={selected.return1Y}>
              {selected.return1YFormatted}
            </PositiveNegativeValue>
          </span>
        </div>
        <div>
          <span className="mr-1" aria-label="7 day change">
            7D
          </span>
          {show7DHighlighting ? (
            <PositiveNegativeValue value={selected.return7D}>
              {selected.return7DFormatted}
            </PositiveNegativeValue>
          ) : (
            <span className="text-solid">{selected.return7DFormatted}</span>
          )}
        </div>
      </div>
    </SmallLayout>
  );
};
